import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';
import { WATCHLIST_KEY } from '../utils/format';

const api = axios.create({ baseURL: '/api', timeout: 15000 });
const authH = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function useWatchlist(isLoggedIn) {
  const [symbols, setSymbols] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchFromServer = useCallback(async () => {
    try {
      const res = await api.get('/watchlist', authH());
      const serverSymbols = res.data;

      // One-time migration: if localStorage has items, push them to server
      const local = JSON.parse(localStorage.getItem(WATCHLIST_KEY) || '[]');
      if (local.length > 0) {
        const merged = [...new Set([...serverSymbols, ...local])];
        await api.put('/watchlist', { symbols: merged }, authH());
        localStorage.removeItem(WATCHLIST_KEY);
        setSymbols(merged);
      } else {
        setSymbols(serverSymbols);
      }
      setLoaded(true);
    } catch {
      // Fallback to localStorage if offline
      setSymbols(JSON.parse(localStorage.getItem(WATCHLIST_KEY) || '[]'));
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) fetchFromServer();
    else { setSymbols([]); setLoaded(false); }
  }, [isLoggedIn, fetchFromServer]);

  // Listen for legacy watchlist-update events (from components not yet migrated)
  useEffect(() => {
    const handler = () => { if (isLoggedIn) fetchFromServer(); };
    window.addEventListener('watchlist-update', handler);
    return () => window.removeEventListener('watchlist-update', handler);
  }, [isLoggedIn, fetchFromServer]);

  const add = useCallback(async (symbol) => {
    const sym = symbol.toUpperCase();
    setSymbols(prev => prev.includes(sym) ? prev : [...prev, sym]);
    try { await api.post('/watchlist', { symbol: sym }, authH()); } catch {}
    window.dispatchEvent(new Event('watchlist-update-internal'));
  }, []);

  const remove = useCallback(async (symbol) => {
    const sym = symbol.toUpperCase();
    setSymbols(prev => prev.filter(s => s !== sym));
    try { await api.delete(`/watchlist/${sym}`, authH()); } catch {}
    window.dispatchEvent(new Event('watchlist-update-internal'));
  }, []);

  const reorder = useCallback(async (newSymbols) => {
    setSymbols(newSymbols);
    try { await api.put('/watchlist', { symbols: newSymbols }, authH()); } catch {}
  }, []);

  const isWatched = useCallback((symbol) => symbols.includes(symbol?.toUpperCase()), [symbols]);

  return { symbols, loaded, add, remove, reorder, isWatched, refresh: fetchFromServer };
}
